import VizExIconButton from 'visitor-ui-component-library/button/VizExIconButton';
import VizExIcon from 'visitor-ui-component-library/icon/VizExIcon';
import { useState, useCallback, useRef } from 'react';
import { SendButton } from '../message-editor/SendButton';
import CloseIcon from '../visitor-widget/CloseIcon';
import styled from 'styled-components';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { EERIE, OBSIDIAN, OLAF } from 'HubStyleTokens/colors';
import I18n from 'I18n';
import VizExFormControl from 'visitor-ui-component-library/form-control/VizExFormControl';
import { adjustLuminance } from 'visitor-ui-component-library/utils/adjustLuminance';
import VizExTooltip from 'visitor-ui-component-library/tooltip/VizExTooltip';
import { useVisuallyHidden, useHover } from 'react-aria';
import { useButton } from '@react-aria/button';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export const DEFAULT_MAX_MESSAGE_LENGTH = 1024;
const AIWelcomeMessageContentWrapper = styled.div.withConfig({
  displayName: "AIWelcomeMessageContent__AIWelcomeMessageContentWrapper",
  componentId: "a4z5ks-0"
})(["position:relative;"]);
const WelcomeMessageText = styled.div.attrs(({
  showWelcomeMessageAsButton
}) => Object.assign({}, showWelcomeMessageAsButton && {
  'aria-haspopup': 'dialog',
  'aria-label': I18n.text('conversations-visitor-experience-components.visitorExperienceAriaLabels.open'),
  'aria-describedby': 'ai-initial-message'
})).withConfig({
  displayName: "AIWelcomeMessageContent__WelcomeMessageText",
  componentId: "a4z5ks-1"
})(["border:", ";display:flex;align-items:center;min-height:", ";width:", ";padding:", ";position:relative;background:", ";border-radius:60px;flex-wrap:nowrap;justify-content:space-between;cursor:", ";"], ({
  theme
}) => `2px solid ${theme.colors.primary}`, ({
  mobile
}) => mobile ? '82px' : '90px', ({
  isAIChatBot,
  mobile
}) => !isAIChatBot ? '312px' : mobile ? '344px' : '400px', ({
  mobile
}) => mobile ? '18px 24px' : '20px 24px', OLAF, ({
  showWelcomeMessageAsButton
}) => showWelcomeMessageAsButton ? 'default' : 'text');
const StyledIconButton = styled(VizExIconButton).withConfig({
  displayName: "AIWelcomeMessageContent__StyledIconButton",
  componentId: "a4z5ks-2"
})(["position:absolute;top:-32px;right:24px;gap:4px;height:24px;width:72px;border-radius:20px;background-color:", ";border:1px solid ", ";font-size:12px;&:hover:enabled{background-color:", ";border-color:", ";}&:active:enabled{background-color:", ";border-color:", ";}"], EERIE, EERIE, adjustLuminance(EERIE, 10), adjustLuminance(EERIE, 10), adjustLuminance(EERIE, -10), adjustLuminance(EERIE, -10));
const InputWrapper = styled.div.withConfig({
  displayName: "AIWelcomeMessageContent__InputWrapper",
  componentId: "a4z5ks-3"
})(["width:100%;align-self:stretch;"]);
const IntialMessageTextWrapper = styled.div.withConfig({
  displayName: "AIWelcomeMessageContent__IntialMessageTextWrapper",
  componentId: "a4z5ks-4"
})(["width:100%;"]);
const StyledParagraph = styled.p.withConfig({
  displayName: "AIWelcomeMessageContent__StyledParagraph",
  componentId: "a4z5ks-5"
})(["outline:none !important;font-size:", ";height:100%;word-break:break-word;border:", ";&:empty{::before{content:'", "';opacity:0.6;}}"], ({
  isIOSMobile
}) => isIOSMobile && '16px', ({
  error
}) => error ? '1px solid red' : 'none', ({
  placeholder
}) => placeholder);
const AIWelcomeMessageContent = ({
  onClose,
  initialMessage,
  publishMessage,
  mobile,
  onClick,
  isAIChatBot,
  isIOSMobile,
  showWelcomeMessageAsButton,
  children
}) => {
  const welcomeMsgTextBoxRef = useRef(null);
  const {
    buttonProps
  } = useButton({
    onPress: onClick,
    elementType: 'div'
  }, welcomeMsgTextBoxRef);
  const inputRef = useRef(null);
  const {
    visuallyHiddenProps
  } = useVisuallyHidden();
  const [showInputField, setShowInputField] = useState(false);
  const [inputMessage, setInputMessage] = useState('');
  const [showTooltip, setShowTooltip] = useState(false);
  const hasTextContent = Boolean(inputMessage.trim());
  const handleInputBlur = useCallback(() => {
    if (!hasTextContent) {
      setShowInputField(false);
    }
  }, [hasTextContent]);
  const {
    hoverProps
  } = useHover({
    onHoverStart: () => {
      if (!hasTextContent && document.activeElement !== inputRef.current) {
        setShowTooltip(true);
      }
    },
    onHoverEnd: () => {
      setShowTooltip(false);
    }
  });
  const inputCallbackRef = useCallback(inputElement => {
    if (inputElement) {
      inputElement.focus();
      inputRef.current = inputElement;
    }
  }, []);
  const messageExceededMaxLength = inputMessage.length > DEFAULT_MAX_MESSAGE_LENGTH;
  const handleChange = e => {
    const {
      innerText
    } = e.target;
    setInputMessage(innerText);
  };
  const handlePublish = () => {
    if (!inputMessage || messageExceededMaxLength) return;
    publishMessage(inputMessage);
  };
  const handleKeyDown = e => {
    if (e.key === 'Enter' && e.shiftKey === false) {
      handlePublish();
      e.preventDefault();
      e.stopPropagation();
      return false;
    }
    return true;
  };
  const handleInputFocus = useCallback(() => {
    setShowTooltip(false);
  }, [setShowTooltip]);
  const handlePaste = event => {
    var _event$clipboardData;
    event.preventDefault();
    const text = event === null || event === void 0 || (_event$clipboardData = event.clipboardData) === null || _event$clipboardData === void 0 ? void 0 : _event$clipboardData.getData('text/plain');
    const selection = window.getSelection();
    if (!(selection !== null && selection !== void 0 && selection.rangeCount)) {
      return;
    }
    selection.deleteFromDocument();
    const node = document.createTextNode(text);
    selection.getRangeAt(0).insertNode(node);
    const textNodes = [];
    const parent = selection.getRangeAt(0).commonAncestorContainer;
    const childNodes = parent.childNodes;
    for (const child of childNodes) {
      textNodes.push(child.nodeValue);
    }
    const inputText = textNodes.join('');
    setInputMessage(inputText || '');
    selection.collapseToEnd();
  };
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [!mobile && /*#__PURE__*/_jsx(VizExTooltip, {
      content: I18n.text('conversations-visitor-experience-components.welcomeMessageTextArea.tooltip'),
      open: showTooltip,
      placement: "top center",
      contentStyle: {
        top: -5
      },
      arrowStyle: {
        top: -15
      },
      children: /*#__PURE__*/_jsx("div", Object.assign({}, visuallyHiddenProps, {
        style: {
          width: 150
        }
      }))
    }), /*#__PURE__*/_jsxs(AIWelcomeMessageContentWrapper, {
      children: [/*#__PURE__*/_jsxs(WelcomeMessageText, Object.assign({
        "data-test-id": "ai-welcome-message-text-box",
        onClick: () => {
          if (showWelcomeMessageAsButton) {
            onClick();
            return;
          }
          setShowInputField(true);
          if (showInputField && inputRef !== null && inputRef !== void 0 && inputRef.current) {
            inputRef.current.focus();
          }
        },
        mobile: mobile,
        isAIChatBot: isAIChatBot,
        showWelcomeMessageAsButton: showWelcomeMessageAsButton,
        tabIndex: 0
      }, mobile || showWelcomeMessageAsButton ? {} : hoverProps, showWelcomeMessageAsButton && buttonProps, {
        children: [showInputField && /*#__PURE__*/_jsx(InputWrapper, {
          children: /*#__PURE__*/_jsx(VizExFormControl, {
            validationMessage: messageExceededMaxLength ? I18n.text('conversations-visitor-experience-components.welcomeMessageTextArea.validationMessage') : '',
            error: messageExceededMaxLength,
            style: {
              height: messageExceededMaxLength ? 'auto' : '100%'
            },
            children: () => /*#__PURE__*/_jsx(StyledParagraph, {
              ref: inputCallbackRef,
              contentEditable: true,
              onInput: handleChange,
              onKeyDown: handleKeyDown,
              onPaste: handlePaste,
              error: messageExceededMaxLength,
              onFocus: handleInputFocus,
              onBlur: handleInputBlur,
              placeholder: I18n.text('conversations-visitor-experience-components.welcomeMessageTextArea.placeholder'),
              tabIndex: 0,
              "data-test-id": "welcome-message-input",
              isIOSMobile: isIOSMobile
            })
          })
        }), !showInputField && /*#__PURE__*/_jsxs(IntialMessageTextWrapper, {
          children: [/*#__PURE__*/_jsx("p", {
            id: "ai-initial-message",
            style: {
              color: OBSIDIAN,
              fontSize: 14,
              lineHeight: '24px',
              margin: 0
            },
            children: initialMessage
          }), isAIChatBot && /*#__PURE__*/_jsx("div", {
            style: {
              color: EERIE,
              fontSize: 12,
              lineHeight: '18px'
            },
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: 'conversations-visitor-experience-components.default.aiAgent'
            })
          })]
        }), !showWelcomeMessageAsButton && /*#__PURE__*/_jsx(SendButton, {
          disabled: messageExceededMaxLength || !hasTextContent,
          style: {
            flexShrink: 0
          },
          onClick: handlePublish,
          className: "m-left-1",
          "data-test-id": "ai-welcome-msg-send-button"
        })]
      })), /*#__PURE__*/_jsxs(StyledIconButton, {
        shape: "circle",
        onClick: onClose,
        "data-test-id": "ai-welcome-msg-close-button",
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: 'conversations-visitor-experience-components.welcomeMessageTextArea.close'
        }), /*#__PURE__*/_jsx(VizExIcon, {
          icon: /*#__PURE__*/_jsx(CloseIcon, {
            "aria-label": I18n.text('conversations-visitor-experience-components.visitorExperienceAriaLabels.dismiss'),
            height: 9,
            width: 9,
            color: OLAF
          })
        })]
      }), children]
    })]
  });
};
AIWelcomeMessageContent.displayName = 'AIWelcomeMessageContent';
export default AIWelcomeMessageContent;